import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import Modal from '../Modal';
import classes from './visitModal.module.scss';
import InputField from '../../Form/TextFields/InputField';
import Select from '../../Form/Selects/Select/Select';
import Button from '../../Form/Buttons/Button';
import { getSelectRepListRequest } from '../../../../store/actions/users';
import SelectWithSearch from '../../Form/Selects/SelectWithSearch/SelectWithSearch';
import DOBPicker from '../../Form/DatePickers/DOBPicker/DOBPicker';
import TimePicker from '../../Form/DatePickers/TimePicker/TimePicker';
import accountHelpers from '../../../../utils/accountHelpers';
import api from '../../../../api/api';
import { addVisitRequest, editVisitRequest } from '../../../../store/actions/physicians';

const limit = 20;
let timeout;

const VisitModal = ({
  isOpenModal, onClose, physicianId = '', physicianName = '', groupName = '', requestAfterSave = null,
  currentDate = moment().add(1, 'day').format('YYYY-MM-DD'), currentTime = '10:00 AM',
  repName = '', id = '', repId = '', disableResetAfterRepSelect,
}) => {
  const isAdmin = accountHelpers.isInOneOfRoles(['Admin', 'Pharmacy']);

  const dispatch = useDispatch();
  const { selectRepList, pageCount, currentPage } = useSelector((store) => store.users);

  const [visitData, setVisitData] = useState({
    Rep: repName || (isAdmin ? '' : accountHelpers.getUser().name),
    RepId: repId || (isAdmin ? '' : accountHelpers.getUser().id),
    PhysicianId: physicianId,
    Physician: physicianName,
    group: groupName,
  });

  const [visitDate, setVisitDate] = useState({
    date: currentDate,
    time: currentTime,
  });

  const [physicianOptions, setPhysicianOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectSearchValue, setSelectSearchValue] = useState({
    physicianValue: physicianName,
    groupValue: groupName,
  });

  const [repError, setRepError] = useState(false);
  const [physicianError, setPhysicianError] = useState(false);
  const [dateError, setDateError] = useState({});
  const [searchLoading, setSearchLoading] = useState('');

  const [addLoading, setAddLoading] = useState(false);

  const {
    Rep, RepId, Physician, group,
  } = visitData;

  const { date, time } = visitDate;

  const { physicianValue, groupValue } = selectSearchValue;

  useEffect(() => {
    if (isAdmin) {
      dispatch(getSelectRepListRequest({ limit, role: 'Rep' }));
    }
  }, []);

  const nextOptionListRequest = useCallback(async (repPage) => {
    await dispatch(getSelectRepListRequest({ page: repPage, limit, role: 'Rep' }));
  }, []);

  const changeVisitData = (path, value, searchValue) => {
    if (path === 'group') {
      setVisitData((prev) => ({ ...prev, [path]: value }));

      setSelectSearchValue((prev) => ({ ...prev, groupValue: searchValue }));
    } else if (path === 'Rep') {
      setRepError(false);

      setVisitData((prev) => ({
        ...prev,
        RepId: value.id,
        Rep: value.name,
        PhysicianId: disableResetAfterRepSelect ? prev.PhysicianId : '',
        Physician: disableResetAfterRepSelect ? prev.Physician : '',
        group: disableResetAfterRepSelect ? prev.group : '',
      }));

      setSelectSearchValue({
        physicianValue: '',
        groupValue: '',
      });
    } else if (path === 'Physician') {
      setPhysicianError(false);

      setVisitData((prev) => ({
        ...prev,
        PhysicianId: value.id,
        Physician: `${value.firstName} ${value.lastName}`,
      }));

      setSelectSearchValue((prev) => ({ ...prev, physicianValue: searchValue }));
    } else {
      setVisitData((prev) => ({ ...prev, [path]: value }));
    }
  };

  const changeVisitDate = (path, value) => {
    setVisitDate((prev) => ({ ...prev, [path]: value }));

    setDateError((prev) => {
      delete prev[path];

      return prev;
    });
  };

  const selectSearch = (path, value) => {
    setSelectSearchValue((prev) => ({ ...prev, [path]: value }));

    clearTimeout(timeout);
    setGroupOptions([]);

    if (value.trim()) {
      setSearchLoading(path);

      timeout = setTimeout(async () => {
        if (path === 'groupValue') {
          const { data: { data } } = await api.getPhysicians({
            searchGroup: value, repId: RepId || accountHelpers.getUser().id, limit: 100,
          });

          const response = _.uniqBy(data.response, 'group');

          setGroupOptions(response);
        } else {
          const userId = RepId || accountHelpers.getUser().id;

          const { data: { data } } = await api.getPhysicians({
            name: value, repId: userId, limit: 100,
          });

          setPhysicianOptions(data.response);
        }

        setSearchLoading('');
      }, 400);
    } else {
      setSearchLoading('');
    }
  };

  const add = async () => {
    let hasError = false;

    if (!RepId) {
      setRepError(true);

      hasError = true;
    }

    if (!Physician) {
      setPhysicianError(true);

      hasError = true;
    }

    if (date && date.length !== 10) {
      setDateError((prev) => ({ ...prev, date: true }));

      hasError = true;
    }

    if (time && time.length !== 8) {
      setDateError((prev) => ({ ...prev, time: true }));

      hasError = true;
    }

    if (hasError) {
      toast.error('Please fill this fields');
    } else {
      const data = {
        ...visitData,
        Rep: Rep || accountHelpers.getUser().name,
        RepId: RepId || (Rep ? null : accountHelpers.getUser().id),
        dateTime: moment.utc(new Date(`${date} ${time}`)).format('YYYY-MM-DD HH:mm'),
      };

      setAddLoading(true);

      try {
        if (id) {
          await dispatch(editVisitRequest({ ...data, id }));
        } else {
          await dispatch(addVisitRequest(data));
        }

        if (requestAfterSave) await requestAfterSave(data.Rep);

        toast.success('New visit successfully added');
      } catch (e) {
        toast.error('Something went wrong');
        console.warn(e);
      }

      setAddLoading(false);
      onClose();
    }
  };

  return (
    <Modal
      open={isOpenModal}
      onClose={onClose}
    >
      <div className={classes.visit_modal_wrapper}>
        <h2>Schedule New Visit</h2>

        <div className={classes.fields_wrapper}>
          <div className={classes.rep_wrapper}>
            {isAdmin
              ? (
                <Select
                  label="Rep"
                  nextOptionListRequest={nextOptionListRequest}
                  options={selectRepList}
                  totalPage={pageCount}
                  currentPage={currentPage}
                  limit={limit}
                  currentOption={Rep}
                  onChange={(option) => changeVisitData('Rep', option)}
                  optionRenderPath="name"
                  zIndex={31}
                  error={repError}
                />
              )
              : (
                <InputField
                  label="Rep"
                  value={Rep}
                  disabled
                />
              )}
          </div>

          <div className={classes.date_wrapper}>
            <DOBPicker
              wrapperClassName={classes.date_picker}
              label="Date"
              hideClearButton
              date={date}
              // minDate={new Date()}
              dateFormat="YYYY/MM/DD"
              onChange={(dateValue) => changeVisitDate('date', dateValue)}
              autoValidation={false}
              error={dateError.date}
            />

            <TimePicker
              wrapperClassName={classes.time_picker}
              label="Time"
              hideClearButton
              time={time}
              onChange={(timeValue) => changeVisitDate('time', timeValue)}
              autoValidation={false}
              error={dateError.time}
            />
          </div>

          {physicianName && !id
            ? (
              <InputField
                label="Physician"
                disabled
                value={Physician}
              />
            )
            : (
              <SelectWithSearch
                label="Physician"
                onChange={(value) => selectSearch('physicianValue', value)}
                loading={searchLoading === 'physicianValue'}
                options={physicianOptions}
                onSelectOption={(option, value) => changeVisitData('Physician', option, value)}
                searchValue={physicianValue}
                optionPathname="fullName"
                optionKey=""
                hideClearButton
                error={physicianError}
                zIndex={31}
              />
            )}

          <div className={classes.group_wrapper}>
            {groupName && !id
              ? (
                <InputField
                  label="Group"
                  disabled
                  value={group}
                />
              )
              : (
                <SelectWithSearch
                  label="Group"
                  onChange={(value) => selectSearch('groupValue', value)}
                  loading={searchLoading === 'groupValue'}
                  options={groupOptions}
                  onSelectOption={(option, value) => changeVisitData('group', option, value)}
                  searchValue={groupValue}
                  optionPathname="group"
                  optionKey="group"
                  zIndex={31}
                />
              )}
          </div>
        </div>

        <div className={classes.btn_wrapper}>
          <Button
            title="Cancel"
            type="button"
            onClick={onClose}
            clearButton
          />

          <Button
            title="Schedule"
            type="button"
            reverseColor
            onClick={add}
            loading={addLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

VisitModal.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  requestAfterSave: PropTypes.func,
  physicianId: PropTypes.string,
  physicianName: PropTypes.string,
  repName: PropTypes.string,
  groupName: PropTypes.string,
  currentDate: PropTypes.string,
  currentTime: PropTypes.string,
  id: PropTypes.string,
  repId: PropTypes.string,
  disableResetAfterRepSelect: PropTypes.bool,
};

export default VisitModal;
